import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@sylviq/ui-react-web/components/ui/card";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import { ArrowDownRight, ArrowUpRight, Check } from "lucide-react";
import { useMemo, useState, type ComponentType, type CSSProperties, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  ChecklistData,
  DataByKind,
  MetricData,
  SeriesData,
  SourceKind,
  Span,
  TableData,
  WidgetConfig,
} from "./model.js";

export type DashboardLink = ComponentType<{
  href: string;
  className?: string;
  children: ReactNode;
}>;

export type OptionValue = string | number | boolean;

export type OptionField =
  | {
      key: string;
      label: string;
      type: "select";
      choices: Array<{ value: string; label: string }>;
      default: string;
    }
  | { key: string; label: string; type: "boolean"; default: boolean }
  | {
      key: string;
      label: string;
      type: "number";
      min: number;
      max: number;
      default: number;
    };

export interface WidgetRenderProps<K extends SourceKind = SourceKind> {
  data: DataByKind[K];
  title: string;
  options: Record<string, OptionValue>;
  Link: DashboardLink;
}

/**
 * A widget type: which kind of source it reads, how it renders, and the
 * options the builder should offer for it. Apps and extensions add their own
 * by passing more definitions to `<Dashboard widgetTypes>`.
 */
export interface WidgetDefinition<K extends SourceKind = SourceKind> {
  type: string;
  label: string;
  kind: K;
  defaultSpan: Span;
  options?: OptionField[];
  /** Return `null` to render nothing at all — the widget drops out of the
   * grid rather than leaving an empty card. */
  render: (props: WidgetRenderProps<K>) => ReactNode;
}

export function resolveOptions(
  definition: WidgetDefinition,
  widget: Pick<WidgetConfig, "options">,
): Record<string, OptionValue> {
  const resolved: Record<string, OptionValue> = {};
  for (const field of definition.options ?? []) {
    const value = widget.options?.[field.key];
    resolved[field.key] =
      typeof value === typeof field.default ? (value as OptionValue) : field.default;
  }
  return resolved;
}

const TREND_TONE = {
  true: "text-[var(--status-success-foreground)]",
  false: "text-[var(--danger)]",
  null: "text-[var(--muted-foreground)]",
} as const;

function MetricWidget({ data, title }: WidgetRenderProps<"metric">) {
  const { value, detail, trend, favorable } = data as MetricData;
  const tone = TREND_TONE[String(favorable ?? null) as keyof typeof TREND_TONE];
  return (
    <Card>
      <CardHeader>
        <div>
          <CardDescription>{title}</CardDescription>
          <p className="mt-2 text-2xl font-semibold tracking-tight tabular-nums">
            {value}
          </p>
        </div>
      </CardHeader>
      <CardContent className="pt-3">
        {detail ? (
          <span className={cn("inline-flex items-center gap-1 text-xs", tone)}>
            {trend === "up" ? <ArrowUpRight className="size-3.5" /> : null}
            {trend === "down" ? <ArrowDownRight className="size-3.5" /> : null}
            {detail}
          </span>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Series colours come from semantic tokens, so a product re-themes charts
 * with the rest of the UI. */
const SERIES_COLOURS = [
  "color-mix(in oklab, var(--status-success-foreground) 80%, var(--action) 20%)",
  "color-mix(in oklab, var(--action) 60%, var(--muted-foreground) 40%)",
  "color-mix(in oklab, var(--status-warning-foreground) 80%, var(--action) 20%)",
  "var(--muted-foreground)",
];

function ChartWidget({ data, title, options }: WidgetRenderProps<"series">) {
  const { x, points, series, formatValue } = data as SeriesData;
  const format = formatValue ?? ((value: number) => String(value));
  const style: CSSProperties = {};
  series.forEach((_, index) => {
    (style as Record<string, string>)[`--series-${index}`] =
      SERIES_COLOURS[index % SERIES_COLOURS.length] ?? "var(--action)";
  });
  const axis = { fill: "var(--muted-foreground)", fontSize: 11 };
  const common = { data: points, margin: { top: 20, right: 8, left: 0, bottom: 4 } };
  const grid = <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />;
  const xAxis = (
    <XAxis dataKey={x} axisLine={false} tickLine={false} tickMargin={10} tick={axis} />
  );
  const yAxis = (
    <YAxis
      axisLine={false}
      tickLine={false}
      width={56}
      tickMargin={6}
      tick={axis}
      tickFormatter={(value) => format(Number(value))}
    />
  );
  const tooltip = (
    <Tooltip
      contentStyle={{
        background: "var(--surface-solid)",
        border: "1px solid var(--border)",
        borderRadius: 12,
      }}
      formatter={(value) => [format(Number(value))]}
    />
  );

  return (
    <Card style={style}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          {series.map((item, index) => (
            <span
              key={item.key}
              className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)]"
            >
              <span
                aria-hidden="true"
                className="size-2.5 rounded-full"
                style={{ background: `var(--series-${index})` }}
              />
              {item.label}
            </span>
          ))}
        </div>
      </CardHeader>
      <CardContent className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          {options.style === "bar" ? (
            <BarChart {...common}>
              {grid}
              {xAxis}
              {yAxis}
              {tooltip}
              {series.map((item, index) => (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  name={item.label}
                  fill={`var(--series-${index})`}
                  radius={[6, 6, 0, 0]}
                />
              ))}
            </BarChart>
          ) : (
            <AreaChart {...common}>
              <defs>
                {series.map((item, index) => (
                  <linearGradient
                    key={item.key}
                    id={`dash-fill-${item.key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={`var(--series-${index})`} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={`var(--series-${index})`} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              {grid}
              {xAxis}
              {yAxis}
              {tooltip}
              {series.map((item, index) => (
                <Area
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={`var(--series-${index})`}
                  fill={`url(#dash-fill-${item.key})`}
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function TableWidget({ data, title, options, Link }: WidgetRenderProps<"table">) {
  const { columns, rows, empty, footer } = data as TableData;
  const [filter, setFilter] = useState("");
  const [sorting, setSorting] = useState<{ key: string; direction: "ascending" | "descending" }>();
  const limit = Number(options.limit);
  const shown = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    const matching = query
      ? rows.filter((row) =>
          Object.values(row.sort ?? {}).some((value) =>
            String(value).toLocaleLowerCase().includes(query),
          ),
        )
      : rows;
    const ordered = sorting
      ? [...matching].sort((left, right) => {
          const leftValue = left.sort?.[sorting.key] ?? "";
          const rightValue = right.sort?.[sorting.key] ?? "";
          const result =
            typeof leftValue === "number" && typeof rightValue === "number"
              ? leftValue - rightValue
              : String(leftValue).localeCompare(String(rightValue), undefined, {
                  numeric: true,
                  sensitivity: "base",
                });
          return sorting.direction === "ascending" ? result : -result;
        })
      : matching;
    return ordered.slice(0, Number.isFinite(limit) && limit > 0 ? limit : ordered.length);
  }, [filter, limit, rows, sorting]);
  const toggleSorting = (key: string) => {
    setSorting((current) =>
      current?.key === key && current.direction === "ascending"
        ? { key, direction: "descending" }
        : { key, direction: "ascending" },
    );
  };
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle>{title}</CardTitle>
        {data.filterable ? (
          <Input
            type="search"
            aria-label={`Filter ${title.toLocaleLowerCase()}`}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter rows…"
            className="mt-2 max-w-xs"
          />
        ) : null}
        {footer ? (
          <Link
            href={footer.href}
            className="text-xs text-[var(--muted-foreground)] underline"
          >
            {footer.label}
          </Link>
        ) : null}
      </CardHeader>
      {shown.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[var(--muted-foreground)]">
          {empty ?? "Nothing to show."}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.align === "end" ? "text-right" : undefined}
                  aria-sort={
                    column.sortable
                      ? sorting?.key === column.key
                        ? sorting.direction
                        : "none"
                      : undefined
                  }
                >
                  {column.sortable ? (
                    <button
                      type="button"
                      className="th-sort"
                      aria-label={`Sort by ${column.label}`}
                      onClick={() => toggleSorting(column.key)}
                    >
                      {column.label}
                      <span aria-hidden="true" className="th-sort__mark">
                        {sorting?.key === column.key && sorting.direction === "descending" ? "↓" : "↑"}
                      </span>
                    </button>
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((row) => (
              <TableRow key={row.id}>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    className={column.align === "end" ? "text-right tabular-nums" : undefined}
                  >
                    {row.cells[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function ChecklistWidget({ data, title, options, Link }: WidgetRenderProps<"checklist">) {
  const { steps, banner, dismissKey } = data as ChecklistData;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return Boolean(
        dismissKey && typeof window !== "undefined" && window.localStorage.getItem(dismissKey),
      );
    } catch {
      return false;
    }
  });
  if (dismissed || (options.hideWhenDone && steps.every((step) => step.done))) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <span className="text-xs text-[var(--muted-foreground)]">
          {steps.filter((step) => step.done).length} of {steps.length} done
        </span>
        {dismissKey ? (
          <button
            type="button"
            className="link-button text-xs"
            onClick={() => {
              try { window.localStorage.setItem(dismissKey, "1"); } catch { /* session-only */ }
              setDismissed(true);
            }}
          >
            Dismiss checklist
          </button>
        ) : null}
      </CardHeader>
      <CardContent>
        {banner ? (
          <div className="mb-5 rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
            <p className="font-medium">{banner.title}</p>
            <p className="mt-1 text-sm text-[var(--muted-foreground)]">{banner.detail}</p>
          </div>
        ) : null}
        <ol className="grid gap-3">
          {steps.map((step, index) => (
            <li key={step.id} className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className={cn(
                  "mt-0.5 grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold",
                  step.done
                    ? "bg-[var(--status-success-background)] text-[var(--status-success-foreground)]"
                    : "bg-[var(--muted)] text-[var(--muted-foreground)]",
                )}
              >
                {step.done ? <Check className="size-3.5" /> : index + 1}
              </span>
              <div className="min-w-0 text-sm">
                <p className="font-medium">
                  {step.label}
                  {step.done ? (
                    <span className="font-normal text-[var(--muted-foreground)]"> — done</span>
                  ) : null}
                </p>
                {step.detail ? (
                  <p className="mt-0.5 text-[var(--muted-foreground)]">
                    {step.detail}{" "}
                    {!step.done && step.href ? (
                      <Link href={step.href} className="font-medium underline">
                        Open
                      </Link>
                    ) : null}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

export const builtInWidgets: WidgetDefinition[] = [
  {
    type: "metric",
    label: "Number",
    kind: "metric",
    defaultSpan: 4,
    render: MetricWidget,
  } as WidgetDefinition<"metric"> as WidgetDefinition,
  {
    type: "chart",
    label: "Chart",
    kind: "series",
    defaultSpan: 12,
    options: [
      {
        key: "style",
        label: "Chart style",
        type: "select",
        choices: [
          { value: "area", label: "Area" },
          { value: "bar", label: "Bars" },
        ],
        default: "area",
      },
    ],
    render: ChartWidget,
  } as WidgetDefinition<"series"> as WidgetDefinition,
  {
    type: "table",
    label: "Table",
    kind: "table",
    defaultSpan: 6,
    options: [
      { key: "limit", label: "Rows to show", type: "number", min: 1, max: 25, default: 5 },
    ],
    render: (props) => <TableWidget {...props} />,
  } as WidgetDefinition<"table"> as WidgetDefinition,
  {
    type: "checklist",
    label: "Checklist",
    kind: "checklist",
    defaultSpan: 12,
    options: [
      { key: "hideWhenDone", label: "Hide once everything is done", type: "boolean", default: true },
    ],
    render: (props) => <ChecklistWidget {...props} />,
  } as WidgetDefinition<"checklist"> as WidgetDefinition,
];
